/**
 * Central registry of all localStorage keys used in GestorPro.
 * Use `getScopedKey(BASE_KEY, uid)` to scope any key to a specific user.
 */

export const STORAGE_KEYS = {
    // localStorage (persistent)
    SETTINGS: 'gemini-fin-settings',
    USER_SESSION: 'gemini-fin-user-session',
    USERS_DB: 'gemini-fin-users-db',
    TRANSACTIONS: 'gemini-fin-transactions',
    RULES: 'gemini-fin-rules',
    GOALS: 'gemini-fin-goals',
    OBJECTIVES: 'gemini-fin-objectives',
    BUDGETS: 'gemini-fin-budgets',
    INVOICES: 'gemini-fin-invoices',
    RESET_CODES: 'gemini-fin-reset-codes',
    GOOGLE_SESSION: 'gemini-fin-google-session',
    SESSION_RESTORE: 'gemini-fin-restore-attempted',
    CURRENT_VIEW: 'gemini-fin-current-view',
    NOTIFICATION_STORE: 'app-notifications',
    GEMINI_QUOTA: 'gemini_quota',
    GLOBAL_SNAPSHOT_CACHE: 'global_snapshot_cache',
    GLOBAL_HISTORY_CACHE: 'global_history_cache',
    GLOBAL_HISTORY_CACHE_TIME: 'global_history_cache_time',
    BEHAVIORAL_PROFILE: 'gestor-behavioral-profile',
    AI_MODEL_ENABLED: 'gestor-ai-model-enabled',
    // Audit 29-07 (session isolation): user-data keys that lived as string
    // literals outside this registry — invisible to the drift-guard, un-scoped
    // and never cleared on logout. Now registered, uid-scoped and wiped on
    // session end via SCOPED_SESSION_CACHE_KEYS.
    DISMISSED_ANOMALIES: 'gestorpro_dismissed_anomalies',
    COMPENSATION_DISMISSALS: 'gestorpro_compensation_dismissals',
    MERCHANT_FEEDBACK: 'gestorpro_merchant_feedback',
    // Purchase intent captured on the public landing BEFORE login — deliberately
    // unscoped (no uid exists yet) and it must survive the register→confirm→login
    // roundtrip, so it must NOT join SCOPED_SESSION_CACHE_KEYS.
    PENDING_PLAN_INTENT: 'gestorpro-pending-plan-intent',
    // C1 analytics: "first_import already reported" device-level flag. Deliberately
    // unscoped (once per BROWSER, not per user) and it must survive logout so the
    // funnel metric is not re-counted — must NOT join SCOPED_SESSION_CACHE_KEYS.
    ANALYTICS_FIRST_IMPORT: 'gestorpro-analytics-first-import',
    // F12 analytics: device-level last-visit timestamp used to compute the
    // aggregated `return_visit` bucket (d1/d7/d30) client-side. A single coarse
    // timestamp, NOT an identifier; never sent to the server (only the bucket
    // label is). Deliberately unscoped and it must survive logout — wiping it on
    // session end would erase the return signal — so it must NOT join
    // SCOPED_SESSION_CACHE_KEYS (see funnelAnalytics.ts).
    ANALYTICS_LAST_VISIT: 'gestorpro-analytics-last-visit',
    // F17: rescue copy of the local edits that LOST a multi-device sync
    // conflict (written by useCloudSync.resolveConflict, scoped via
    // getScopedKey → `gestorpro_conflict_backup_<uid>`). Deliberately EXCLUDED
    // from the logout/inactivity purges: it holds the user's own data, already
    // present on this device, and wiping it would destroy the only surviving
    // copy. Compensating control: 7-day self-expiry purged on app start — full
    // lifecycle in services/conflictBackupService.ts.
    CONFLICT_BACKUP: 'gestorpro_conflict_backup',
    // F20: the user chose "set up encryption later". Scoped per uid via
    // getScopedKey. It lives in localStorage and NOT in settings on purpose —
    // while the deferral is active the app writes nothing to the cloud (see
    // useCloudSync), so persisting the choice through settings would be
    // impossible. Cleared as soon as the passphrase exists.
    ENCRYPTION_SETUP_DEFERRED: 'gestorpro_encryption_setup_deferred',
    // sessionStorage (tab-scoped)
    SESSION_SETTINGS_TAB: 'gestorpro_settings_active_tab',
    SESSION_DATA_MGT_TAB: 'gestorpro_data_management_tab',
    SESSION_SELECTED_ACCOUNT: 'gestorpro_selected_account_id',
    SESSION_PROCESSING_RESULT: 'gestorpro_processing_result',
    FACTORY_RESET_IN_PROGRESS: 'FACTORY_RESET_IN_PROGRESS',
} as const;

/**
 * Returns a user-scoped key: `${baseKey}_${uid}` when uid is available.
 * Falls back to the generic key if uid is null/undefined.
 */
export const getScopedKey = (baseKey: string, uid: string | null | undefined): string =>
    uid ? `${baseKey}_${uid}` : baseKey;
